import type { World } from './world.js';
import type { EventQueue } from './events.js';
import type { EngineConfig, WeaponLevelDef } from '../types.js';
import { deg2rad } from '../math.js';

const HOMING_ARM_MS = 200;

/**
 * Update player weapon cooldowns and fire bullets.
 * CRITICAL: all player bullets travel straight up (negative Y).
 * Spread weapon fans symmetrically around (0, -1).
 */
export function updatePlayerWeapons(
  world: World,
  dtMs: number,
  config: EngineConfig,
  events: EventQueue
): void {
  for (const player of world.players) {
    if (!player.alive) continue;
    // Cannot fire during respawn delay.
    if (player.respawnMs > 0) continue;

    const weaponDef = config.weapons[player.weapon.kind];
    const level: WeaponLevelDef = weaponDef.levels[player.weapon.level - 1];

    if (player.weapon.fireCooldownMs > 0) {
      player.weapon.fireCooldownMs = Math.max(0, player.weapon.fireCooldownMs - dtMs);
    }
    if (player.weapon.fireCooldownMs > 0) continue;

    const cadenceMs = 1000 / level.fireRateHz;
    player.weapon.fireCooldownMs = cadenceMs;

    const muzzle = { x: player.pos.x, y: player.pos.y - player.radius };

    switch (player.weapon.kind) {
      case 'spread': {
        const count = Math.max(1, level.bulletsPerShot ?? 1);
        const spread = level.spreadDeg ?? 0;
        const speed = level.bulletSpeed;
        // Angles symmetric around straight-up (0,-1).
        // For count=1, single bullet at 0deg.
        // For count>1, distribute across [-spread/2, +spread/2].
        for (let i = 0; i < count; i++) {
          let tDeg = 0;
          if (count > 1) {
            const step = spread / (count - 1);
            tDeg = -spread / 2 + step * i;
          }
          const rad = deg2rad(tDeg);
          // Up vector rotated by tDeg around z.
          // Base up = (0, -1). Rotated: (sin(rad), -cos(rad)).
          const vx = Math.sin(rad) * speed;
          const vy = -Math.cos(rad) * speed;
          world.spawnBullet({
            pos: muzzle,
            vel: { x: vx, y: vy },
            team: 'player',
            kind: 'spread',
            damage: level.damage,
          });
        }
        events.push({ type: 'shoot', weapon: 'spread', pos: { x: muzzle.x, y: muzzle.y } });
        break;
      }
      case 'laser': {
        const speed = level.bulletSpeed;
        world.spawnBullet({
          pos: muzzle,
          vel: { x: 0, y: -speed },
          team: 'player',
          kind: 'laser',
          damage: level.damage,
          pierce: level.pierce ?? 1,
          radius: 5,
        });
        events.push({ type: 'shoot', weapon: 'laser', pos: { x: muzzle.x, y: muzzle.y } });
        break;
      }
      case 'homing': {
        const speed = level.bulletSpeed;
        const turnDegPerSec = level.homingTurnDegPerSec ?? 180;
        world.spawnBullet({
          pos: muzzle,
          vel: { x: 0, y: -speed },
          team: 'player',
          kind: 'homing',
          damage: level.damage,
          armMs: HOMING_ARM_MS,
          turnRate: deg2rad(turnDegPerSec),
          targetId: -1,
        });
        events.push({ type: 'shoot', weapon: 'homing', pos: { x: muzzle.x, y: muzzle.y } });
        break;
      }
    }
  }
}

/**
 * Update homing bullets: once armed, steer toward nearest enemy.
 */
export function updateHomingBullets(world: World, dtMs: number): void {
  const dt = dtMs / 1000;
  for (const b of world.bullets) {
    if (!b.alive) continue;
    if (b.kind !== 'homing') continue;
    if (b.armMs > 0) continue;

    // Find target if missing or dead.
    let target = null as null | { id: number; pos: { x: number; y: number } };
    if (b.targetId !== -1) {
      for (const e of world.enemies) {
        if (e.alive && e.id === b.targetId) {
          target = e;
          break;
        }
      }
    }
    if (!target) {
      // Re-acquire nearest.
      let best: { id: number; pos: { x: number; y: number } } | null = null;
      let bestD = Infinity;
      for (const e of world.enemies) {
        if (!e.alive) continue;
        const dx = e.pos.x - b.pos.x;
        const dy = e.pos.y - b.pos.y;
        const d = dx * dx + dy * dy;
        if (d < bestD) {
          bestD = d;
          best = e;
        }
      }
      if (best) {
        b.targetId = best.id;
        target = best;
      } else {
        continue;
      }
    }

    // Steer velocity toward target by at most turnRate * dt radians.
    const desiredX = target.pos.x - b.pos.x;
    const desiredY = target.pos.y - b.pos.y;
    const desiredLen = Math.sqrt(desiredX * desiredX + desiredY * desiredY);
    if (desiredLen === 0) continue;
    const dX = desiredX / desiredLen;
    const dY = desiredY / desiredLen;

    const speed = Math.sqrt(b.vel.x * b.vel.x + b.vel.y * b.vel.y);
    if (speed === 0) continue;
    const vX = b.vel.x / speed;
    const vY = b.vel.y / speed;

    // angle between current and desired
    const dot = Math.max(-1, Math.min(1, vX * dX + vY * dY));
    const angle = Math.acos(dot);
    const maxTurn = b.turnRate * dt;
    const t = angle === 0 ? 0 : Math.min(1, maxTurn / angle);

    // Slerp-ish: lerp direction then renormalize.
    const nx = vX + (dX - vX) * t;
    const ny = vY + (dY - vY) * t;
    const nlen = Math.sqrt(nx * nx + ny * ny);
    if (nlen === 0) continue;
    b.vel.x = (nx / nlen) * speed;
    b.vel.y = (ny / nlen) * speed;
  }
}
